/**
 * WS-NAV — `/home`, the workspace overview.
 *
 * Greeting + one card per workspace the caller is offered. Always reachable
 * (the switcher's first entry), so it never redirects on its own — the two
 * landing rules only fire when the app *lands* here after sign-in, which the
 * `?landing=1` flag marks:
 *
 *   - a caller with a single workspace skips Home entirely,
 *   - a returning caller goes to their last-used workspace.
 *
 * Badge counts are deliberately absent: the brief allows static cards when no
 * cheap existing endpoint fits, and none of the six workspaces has one that
 * would not mean new backend work.
 */
import type { ReactElement } from "react";
import { Box, Card, CardActionArea, Stack, Typography } from "@mui/material";
import { Navigate, useSearchParams } from "react-router-dom";
import { useMe } from "@aeriosiq/auth-client";
import { PageHeading, typography, workspaceAccent } from "@aeriosiq/ui";
import { HOME_PATH } from "../workspaces/resolve.js";
import {
  useActiveWorkspace,
  useLandingPath,
} from "../workspaces/useWorkspaces.js";
import { shortcutHint } from "../chrome/workspaceShortcuts.js";
import type { Workspace } from "../workspaces/types.js";
import { ArrowRightIcon } from "@aeriosiq/ui/icons";

function greeting(hour: number): string {
  if (hour < 5) return "Good night";
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

function firstName(displayName: string | undefined): string | null {
  if (!displayName) return null;
  const [first] = displayName.trim().split(/\s+/);
  return first && first.length > 0 ? first : null;
}

function WorkspaceCard({
  workspace,
  onOpen,
}: {
  workspace: Workspace;
  onOpen: (workspace: Workspace) => void;
}): ReactElement {
  const isPlatform = workspace.accent === "platform";
  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        ...(isPlatform
          ? {
              borderColor: (t) =>
                t.palette.mode === "dark"
                  ? workspaceAccent.dark.solid
                  : workspaceAccent.light.solid,
            }
          : {}),
      }}
    >
      <CardActionArea
        onClick={() => {
          onOpen(workspace);
        }}
        aria-label={`${workspace.label} — ${workspace.tagline}`}
        data-workspace-card={workspace.id}
        sx={{ height: "100%", p: 2.5, display: "block" }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            aria-hidden
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              flexShrink: 0,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: (t) =>
                isPlatform
                  ? t.palette.mode === "dark"
                    ? workspaceAccent.dark.tint
                    : workspaceAccent.light.tint
                  : t.palette.action.hover,
              color: (t) =>
                isPlatform
                  ? t.palette.mode === "dark"
                    ? workspaceAccent.dark.fg
                    : workspaceAccent.light.fg
                  : t.palette.primary.main,
            }}
          >
            <workspace.Icon />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography
                component="h2"
                sx={{
                  fontSize: typography.size["title-md"],
                  fontWeight: typography.weight.semibold,
                  color: (t) =>
                    isPlatform
                      ? t.palette.mode === "dark"
                        ? workspaceAccent.dark.fg
                        : workspaceAccent.light.fg
                      : "text.primary",
                }}
              >
                {workspace.label}
              </Typography>
              <Typography
                component="span"
                aria-hidden
                sx={{
                  color: "text.secondary",
                  fontSize: typography.size["caption-sm"],
                  whiteSpace: "nowrap",
                }}
              >
                {shortcutHint(workspace.shortcut)}
              </Typography>
            </Stack>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mt: 0.5 }}
            >
              {workspace.tagline}
            </Typography>
            <ArrowRightIcon
              aria-hidden
              fontSize="small"
              sx={{ mt: 1.5, color: "text.tertiary" }}
            />
          </Box>
        </Stack>
      </CardActionArea>
    </Card>
  );
}

export function HomePage(): ReactElement {
  const { visible, navigateToWorkspace } = useActiveWorkspace();
  const me = useMe();
  const [params] = useSearchParams();
  const landingPath = useLandingPath();

  // Landing rules fire once, on arrival from sign-in. An explicit visit to
  // Home (switcher, brand mark, bookmark) always renders the cards.
  const isLanding = params.get("landing") === "1";
  if (isLanding && landingPath !== HOME_PATH) {
    return <Navigate to={landingPath} replace />;
  }

  const name = firstName(me.data?.displayName);
  const hello = `${greeting(new Date().getHours())}${name ? `, ${name}` : ""}`;

  return (
    <Stack spacing={3} sx={{ maxWidth: 1040 }}>
      <Box>
        <PageHeading>{hello}</PageHeading>
        <Typography variant="body1" sx={{ color: "text.secondary", mt: 0.5 }}>
          {visible.length === 1
            ? "Your workspace"
            : "Pick a workspace to get started. You can switch at any time."}
        </Typography>
      </Box>
      <Box
        // mobile_strategy §2.1 — the card grid stacks on a phone.
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(3, minmax(0, 1fr))",
          },
        }}
      >
        {visible.map((w) => (
          <WorkspaceCard
            key={w.id}
            workspace={w}
            // Same reason as the switcher: a destination two workspaces own
            // cannot say which one the user meant, so the choice rides along.
            onOpen={navigateToWorkspace}
          />
        ))}
      </Box>
    </Stack>
  );
}
